import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { get, post, put, del, normalize } from "../forum/api_client"
import { loadTranslations, t } from "../forum/i18n"
import { escapeHtml, loadingSkeletonHtml, paginationHtml, avatarHtml, formatDate } from "../forum/renderer"

export default class extends Controller {
  static targets = ["content", "repliesContainer", "replyForm", "pagination"]
  static values = { topicId: String, currentUserId: String }

  connect() {
    loadTranslations()
    this.currentPage = 1
    this.perPage = parseInt(localStorage.getItem('forum_per_page'), 10) || 25
    this.sortOrder = 'asc'
    this.topicData = null
    this.load()
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
    }
  }

  subscribeToChannel() {
    if (this.channel) this.channel.unsubscribe()

    this.channel = createConsumer().subscriptions.create(
      { channel: "ForumTopicChannel", topic_id: this.topicIdValue },
      {
        received: (data) => this.handleBroadcast(data)
      }
    )
  }

  handleBroadcast(data) {
    if (data.action === 'new_post' && data.post) {
      const newPost = normalize(data.post)
      if (!newPost) return
      if (newPost.author?.id === this.currentUserIdValue) return
      if (document.getElementById(`post-${newPost.id}`)) return
      this.appendPost(newPost)
    } else if (data.action === 'reaction_updated' && data.post_id) {
      this.updateReactionsFromBroadcast(data.post_id, data.reactions_summary || {})
    }
  }

  updateReactionsFromBroadcast(postId, newSummary) {
    const container = this.element.querySelector(`[data-post-reactions="${postId}"]`)

    if (container) {
      const state = JSON.parse(container.dataset.reactionState || '{}')
      state.summary = newSummary
      container.dataset.reactionState = JSON.stringify(state)
      container.innerHTML = this.renderReactionButtons(
        newSummary, state.userReactions || [], postId, state.canReact || false, state.isOwnPost || false
      )
      this.bindReactionEvents()
    } else {
      const postBlock = document.getElementById(`post-${postId}`)
      if (!postBlock) return

      const hasAnyReaction = Object.values(newSummary).some(v => v > 0)
      if (!hasAnyReaction) return

      const isLoggedIn = !!this.currentUserIdValue
      const canReact = false
      const isOwnPost = true
      const state = { summary: newSummary, userReactions: [], canReact, isOwnPost }
      const inner = this.renderReactionButtons(newSummary, [], postId, canReact, isOwnPost)
      if (!inner) return

      const reactionsDiv = document.createElement('div')
      reactionsDiv.className = 'forum-reactions'
      reactionsDiv.dataset.postReactions = postId
      reactionsDiv.dataset.reactionState = JSON.stringify(state)
      reactionsDiv.innerHTML = inner

      const controlsLeft = postBlock.querySelector('.forum-post-controls__left')
      if (controlsLeft) {
        controlsLeft.prepend(reactionsDiv)
      } else {
        const postBody = postBlock.querySelector('.forum-post-body')
        if (!postBody) return
        let controlsDiv = postBody.querySelector('.forum-post-controls')
        if (!controlsDiv) {
          controlsDiv = document.createElement('div')
          controlsDiv.className = 'forum-post-controls mt-3'
          const innerDiv = document.createElement('div')
          innerDiv.className = 'd-flex align-items-center gap-2'
          innerDiv.appendChild(reactionsDiv)
          controlsDiv.appendChild(innerDiv)
          postBody.appendChild(controlsDiv)
        } else {
          controlsDiv.querySelector('.d-flex')?.prepend(reactionsDiv)
        }
      }
      this.bindReactionEvents()
    }
  }

  appendPost(postData) {
    const canReply = this.topicMeta?.can_reply || false
    const repliesList = document.getElementById('forum-replies-list')
    const repliesSection = document.getElementById('replies-section')

    if (!repliesList || !repliesSection) {
      this.load()
      return
    }

    const hasExisting = repliesList.children.length > 0
    const separator = hasExisting ? '<hr class="forum-post-separator">' : ''
    const postHtml = separator + this.renderPost(postData, this.topicData, canReply)
    repliesList.insertAdjacentHTML('beforeend', postHtml)

    const countEl = repliesSection.querySelector('span')
    if (countEl && this.topicData) {
      const newCount = (this.topicData.posts_count || 1)
      this.topicData.posts_count = newCount + 1
      countEl.textContent = t('topic.replies_in_topic', { count: newCount })
    }

    this.bindEvents()

    const newEl = document.getElementById(`post-${postData.id}`)
    if (newEl) {
      newEl.classList.add('forum-post-highlight')
      setTimeout(() => newEl.classList.remove('forum-post-highlight'), 3000)
    }
  }

  async load() {
    this.contentTarget.innerHTML = loadingSkeletonHtml(8)
    try {
      const [topicResp, postsResp] = await Promise.all([
        get(`/topics/${this.topicIdValue}`),
        get(`/topics/${this.topicIdValue}/posts`, { page: this.currentPage, per_page: this.perPage })
      ])
      topicResp.data = normalize(topicResp.data)
      topicResp.first_post = normalize(topicResp.first_post)
      postsResp.data = normalize(postsResp.data)
      this.topicData = topicResp.data
      this.topicMeta = topicResp.meta
      this.firstPost = topicResp.first_post
      this.render(topicResp, postsResp)
    } catch (e) {
      this.contentTarget.innerHTML = `<div class="alert alert-danger">${escapeHtml(e.message)}</div>`
    }
  }

  render(topicResp, postsResp) {
    const topic = topicResp.data
    const meta = topicResp.meta
    const firstPost = topicResp.first_post
    const posts = postsResp.data || []
    const postsMeta = postsResp.meta || {}
    const canReply = meta.can_reply

    const allReplies = Array.isArray(posts) ? posts : (posts ? [posts] : [])
    const replies = allReplies.filter(p => !p.is_first_post && p.post_number !== 1)
    const repliesCount = Math.max(0, (topic.posts_count || 1) - 1)

    let html = ''
    html += this.renderTopicBlock(topic, firstPost, canReply, meta)
    html += this.renderRepliesSection(replies, repliesCount, postsMeta, canReply)
    html += this.renderReplyForm(topic, canReply)
    html += this.renderModals(topic)

    this.contentTarget.innerHTML = html
    this.bindEvents()
  }

  renderTopicBlock(topic, firstPost, canReply, meta = {}) {
    let firstPostHtml = ''
    if (firstPost) {
      firstPostHtml = `<div class="forum-posts-list">${this.renderPost(firstPost, topic, canReply, true)}</div>`
    }

    const editTitleBtn = meta.can_update ? `<button class="btn btn-sm btn-outline-secondary js-edit-topic-title" type="button" title="${t('topic.edit_title', { defaultValue: 'Edytuj tytuł' })}"><i class="fa-solid fa-pen"></i></button>` : ''
    const modControls = (meta.can_update || meta.can_close || meta.can_archive || meta.can_delete) ? `
      <div class="d-flex align-items-center gap-2">
        ${editTitleBtn}
        <div class="dropdown">
          <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
            ${t('moderation.actions', { defaultValue: 'Moderacja' })}
          </button>
          <ul class="dropdown-menu">
            ${meta.can_close && topic.status !== 'closed' && topic.status !== 'archived' ? `<li><button class="dropdown-item js-topic-close" type="button"><i class="fa-solid fa-ban me-2"></i>${t('moderation.close', { defaultValue: 'Zamknij temat' })}</button></li>` : ''}
            ${meta.can_close && (topic.status === 'closed' || topic.status === 'archived') ? `<li><button class="dropdown-item js-topic-reopen" type="button"><i class="fa-solid fa-lock-open me-2"></i>${t('moderation.reopen', { defaultValue: 'Otwórz temat' })}</button></li>` : ''}
            ${meta.can_archive && topic.status !== 'archived' ? `<li><button class="dropdown-item js-topic-archive" type="button"><i class="fa-solid fa-box-archive me-2"></i>${t('moderation.archive', { defaultValue: 'Archiwizuj' })}</button></li>` : ''}
            ${meta.can_delete ? `<li><hr class="dropdown-divider"></li><li><button class="dropdown-item text-danger js-topic-delete" type="button"><i class="fa-solid fa-trash me-2"></i>${t('moderation.delete_topic', { defaultValue: 'Usuń temat (superadmin)' })}</button></li>` : ''}
          </ul>
        </div>
      </div>` : ''

    const archivedBanner = topic.status === 'archived'
      ? `<div class="alert alert-warning d-flex align-items-center gap-2 mb-0 rounded-0 border-0 border-bottom" role="status">
          <i class="fa-solid fa-box-archive fs-5"></i>
          <span><strong>${t('status.archived', { defaultValue: 'Temat archiwalny' })}</strong> – ${t('status.archived_hint', { defaultValue: 'widoczny tylko dla superadmina' })}</span>
        </div>`
      : ''

    return `
      <div class="forum-category-block mb-3${topic.status === 'archived' ? ' forum-topic-archived' : ''}">
        ${archivedBanner}
        <div class="forum-topic-header__inner p-3 border-bottom d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h4 class="forum-topic-title mb-0">
              ${topic.status === 'archived' ? `<span class="badge bg-warning text-dark me-2"><i class="fa-solid fa-box-archive me-1"></i>${t('status.archived_short', { defaultValue: 'Archiwum' })}</span>` : ''}
              ${escapeHtml(topic.title)}
            </h4>
            <div class="forum-topic-meta text-muted small">
              ${t('forum_table.views_count', { count: topic.views_count || 0 })}
            </div>
          </div>
          ${modControls}
        </div>
        ${firstPostHtml}
      </div>`
  }

  renderPost(p, topic, canReply, isFirst = false) {
    const author = p.author || {}
    const avatarEl = avatarHtml(author)
    const role = author.role ? t(`roles.${author.role}`) : t('roles.member')
    const postsCount = author.posts_count || 0
    const joinedAt = author.joined_at ? formatDate(author.joined_at, 'short') : ''

    const isTopicAuthor = !isFirst && author.id === topic.author?.id
    const authorBadge = isTopicAuthor
      ? `<span class="badge bg-light text-muted border ms-2">${t('topic.author')}</span>`
      : ''

    let replyToHtml = ''
    if (p.reply_to) {
      const rt = p.reply_to
      replyToHtml = `
        <div class="forum-quote-reference mb-3">
          <a class="forum-quote-ref-link d-flex align-items-start text-decoration-none border-start border-3 border-secondary bg-light p-2 rounded-end" href="#post-${rt.id}">
            <i class="fa-solid fa-reply fa-flip-horizontal text-muted me-2 mt-1"></i>
            <div class="forum-quote-ref-content">
              <div class="forum-quote-ref-author fw-bold small">${escapeHtml(rt.author || '')}</div>
              <div class="forum-quote-ref-text text-muted small fst-italic">${escapeHtml(rt.excerpt || '')}</div>
            </div>
          </a>
        </div>`
    }

    const editedHtml = (p.edit_count || 0) > 0
      ? `<div class="forum-post-edited small text-muted mt-2">${t('post.edited', { count: p.edit_count })}</div>`
      : ''

    const reactionsHtml = this.renderReactions(p)

    const canEdit = p.can_edit === true
    const editBtn = canEdit ? `<a class="forum-post-control-link js-edit-post" href="#" data-post-id="${p.id}"><i class="fa-solid fa-pen me-1"></i>${t('post.edit', { defaultValue: 'Edytuj' })}</a>` : ''
    const controlsHtml = canReply ? `
      <div class="forum-post-controls d-flex justify-content-between align-items-center mt-3">
        <div class="forum-post-controls__left d-flex align-items-center gap-2">
          ${reactionsHtml}
        </div>
        <div class="forum-post-controls__right d-flex gap-3">
          ${editBtn}
          <a class="forum-post-control-link text-muted js-report-post" href="#" data-post-id="${p.id}">
            <i class="fa-solid fa-flag me-1"></i>${t('post.report')}
          </a>
          <a class="forum-post-control-link js-quote-post" href="#reply-form" data-post-id="${p.id}" data-author="${escapeHtml(author.full_name || '')}" data-content="${escapeHtml((p.raw_content || '').replace(/<[^>]*>/g, '').substring(0, 200))}">
            <i class="fa-solid fa-quote-left me-1"></i>${t('post.quote')}
          </a>
          <a class="forum-post-control-link" href="#reply-form">
            <i class="fa-solid fa-reply me-1"></i>${t('add_reply')}
          </a>
        </div>
      </div>` : `<div class="forum-post-controls mt-3"><div class="d-flex align-items-center gap-2">${reactionsHtml}${editBtn}</div></div>`

    return `
      <div class="forum-post-block" id="post-${p.id}">
        <div class="forum-post-wrap">
          <div class="forum-post-header">
            <div class="forum-post-header__left">
              <span class="forum-post-username"><span class="fw-bold">${escapeHtml(author.full_name || t('unknown'))}</span></span>
              <span class="forum-post-date text-muted small ms-2">${t('post.written_on', { date: formatDate(p.created_at) })}</span>
              ${authorBadge}
            </div>
            <div class="forum-post-header__right d-flex align-items-center gap-2">
              <a class="forum-post-id" href="#post-${p.id}" title="${t('post.permalink')}">#${p.post_number}</a>
              <a class="forum-share-link text-muted js-share-post" href="#" data-share-url="${window.location.origin}/forum/topics/${topic.id}#post-${p.id}" title="${t('post.share')}">
                <i class="fa-solid fa-share-nodes"></i>
              </a>
            </div>
          </div>
          <div class="forum-post-content d-flex">
            <div class="forum-author-info">
              <div class="forum-author-avatar mb-2">${avatarEl}</div>
              <div class="forum-author-meta text-center small">
                <div class="forum-author-role text-muted">${escapeHtml(role)}</div>
                <div class="forum-author-posts mt-1">${t('post_count', { count: postsCount })}</div>
                <div class="forum-author-joined mt-1 text-muted">${t('joined_on', { date: joinedAt })}</div>
              </div>
            </div>
            <div class="forum-post-body">
              ${replyToHtml}
              <div class="forum-post-content-inner">${p.rendered_content || p.raw_content || ''}</div>
              ${editedHtml}
              ${controlsHtml}
            </div>
          </div>
        </div>
      </div>`
  }

  static REACTIONS = [
    { type: 'like', icon: 'fa-thumbs-up', primary: true },
    { type: 'heart', icon: 'fa-heart', primary: true },
    { type: 'funny', icon: 'fa-face-laugh', primary: true },
    { type: 'insightful', icon: 'fa-lightbulb', primary: false },
    { type: 'disagree', icon: 'fa-thumbs-down', primary: false }
  ]

  renderReactions(p) {
    const summary = p.reactions_summary || {}
    const userReactions = p.current_user_reactions || []
    const isLoggedIn = !!this.currentUserIdValue
    const isOwnPost = isLoggedIn && p.author?.id === this.currentUserIdValue
    const canReact = isLoggedIn && !isOwnPost

    const inner = this.renderReactionButtons(summary, userReactions, p.id, canReact, isOwnPost)

    if (!inner) return ''

    const stateJson = JSON.stringify({ summary, userReactions, canReact, isOwnPost })
    return `<div class="forum-reactions" data-post-reactions="${p.id}" data-reaction-state='${stateJson}'>${inner}</div>`
  }

  renderReactionButtons(summary, userReactions, postId, canReact, isOwnPost) {
    const visibleReactions = this.constructor.REACTIONS.filter(r => {
      const count = summary[r.type] || 0
      if (r.primary) return canReact || count > 0
      return count > 0
    })

    if (visibleReactions.length === 0 && !canReact) return ''

    const btns = visibleReactions.map(r => {
      const count = summary[r.type] || 0
      const active = userReactions.includes(r.type) ? ' active' : ''
      const countHtml = count > 0 ? `<span class="forum-reaction-count">${count}</span>` : ''
      const disabled = canReact ? '' : ' disabled'
      const label = t(`reaction_types.${r.type}`)
      return `<button class="forum-reaction-btn${active}${disabled}" data-post-id="${postId}" data-reaction-type="${r.type}" title="${label}" type="button"><i class="fa-solid ${r.icon}"></i>${countHtml}</button>`
    }).join('')

    let moreBtn = ''
    if (canReact) {
      const dropdownReactions = this.constructor.REACTIONS.filter(r => !r.primary)
      const moreItems = dropdownReactions.map(r => {
        const count = summary[r.type] || 0
        const active = userReactions.includes(r.type) ? ' active' : ''
        const countHtml = count > 0 ? ` <span class="forum-reaction-count">${count}</span>` : ''
        const label = t(`reaction_types.${r.type}`)
        return `<li><button class="dropdown-item forum-reaction-dropdown-item${active}" data-post-id="${postId}" data-reaction-type="${r.type}" type="button"><i class="fa-solid ${r.icon} me-2"></i>${label}${countHtml}</button></li>`
      }).join('')

      moreBtn = `
        <div class="dropdown d-inline-block">
          <button class="forum-reaction-btn forum-reaction-more" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="...">
            <i class="fa-solid fa-ellipsis"></i>
          </button>
          <ul class="dropdown-menu forum-reaction-dropdown">${moreItems}</ul>
        </div>`
    }

    return btns + moreBtn
  }

  renderRepliesSection(replies, repliesCount, postsMeta, canReply) {
    if (repliesCount === 0) return ''

    const sortAscClass = this.sortOrder === 'asc' ? 'fw-bold' : 'text-muted'
    const sortDescClass = this.sortOrder === 'desc' ? 'fw-bold' : 'text-muted'

    const sortControls = `
      <div class="forum-sort-control">
        <a href="#" class="text-decoration-none js-sort ${sortAscClass}" data-sort="asc">
          <i class="fa-solid fa-arrow-up-short-wide me-1"></i>${t('sort.oldest_first')}
        </a> /
        <a href="#" class="text-decoration-none js-sort ${sortDescClass}" data-sort="desc">
          <i class="fa-solid fa-arrow-down-wide-short me-1"></i>${t('sort.newest_first')}
        </a>
      </div>
      <div class="forum-per-page-control">
        ${[10, 25, 50].map(n =>
          `<a href="#" class="text-decoration-none mx-1 js-per-page ${this.perPage === n ? 'fw-bold' : 'text-muted'}" data-per-page="${n}">${n}</a>`
        ).join('')}
      </div>`

    const postsHtml = replies.map((p, i) => {
      const sep = i > 0 ? '<hr class="forum-post-separator">' : ''
      return sep + this.renderPost(p, this.topicData, canReply)
    }).join('')

    const pagination = paginationHtml(postsMeta)

    return `
      <div class="forum-category-block mb-0" id="replies-section">
        <div class="forum-maintitle d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>${t('topic.replies_in_topic', { count: repliesCount })}</span>
          <div class="forum-replies-controls d-flex align-items-center gap-3 small">${sortControls}</div>
        </div>
      </div>
      <div id="replies-list-container">
        <div class="forum-category-block border-top-0">
          <div class="forum-posts-list" id="forum-replies-list">${postsHtml}</div>
          <div data-forum-topic-target="pagination">${pagination}</div>
        </div>
      </div>`
  }

  renderReplyForm(topic, canReply) {
    if (!canReply) {
      const isLockedOrClosed = topic.locked ||
        topic.status === 'closed' || topic.status === 'archived' ||
        topic.status === 1 || topic.status === 2
      if (isLockedOrClosed) {
        return `<div class="alert alert-info mt-4">${t('topic.locked_or_closed')}</div>`
      }
      return `<div class="alert alert-info mt-4">${t('login_to_post')}</div>`
    }

    return `
      <div class="forum-category-block mt-4" id="reply-form" data-controller="forum-reply-editor">
        <h3 class="forum-maintitle">${t('add_reply')}</h3>
        <div class="p-3">
          <form id="forum-reply-form" data-action="submit->forum-topic#submitReply">
            <input type="hidden" name="reply_to_id" id="reply_to_id" value="">
            <div id="quote-container" class="d-none mb-3">
              <div class="forum-quote border-start border-3 border-secondary bg-light p-3 rounded-end">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="forum-quote__content">
                    <a class="forum-quote__author fw-bold text-decoration-none" href="#" id="quote-link"></a>
                    <div class="forum-quote__text text-muted fst-italic mt-1" id="quote-text"></div>
                  </div>
                  <button class="btn-close btn-sm js-remove-quote" type="button">
                    <span class="visually-hidden">${t('close', { defaultValue: 'Zamknij' })}</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label for="forum_post_content_format" class="form-label">${t('content_format', { defaultValue: 'Format treści' })}</label>
              <select class="form-select mb-2" name="content_format" id="forum_post_content_format">
                <option value="html" selected>${t('content_formats.html', { defaultValue: 'HTML' })}</option>
                <option value="markdown">${t('content_formats.markdown', { defaultValue: 'Markdown' })}</option>
              </select>
            </div>
            <div class="mb-3">
              <label for="forum_post_raw_content" class="form-label">${t('post.content')}</label>
              <textarea class="form-control" rows="6" placeholder="${t('post.content')}" id="forum_post_raw_content" name="raw_content" required></textarea>
            </div>
            <div class="text-end">
              <button class="default-btn reverse" type="submit" id="forum-reply-submit-btn"><span>${t('reply')}</span></button>
            </div>
          </form>
        </div>
      </div>`
  }

  renderModals(topic) {
    return `
      <div class="modal fade" id="editTopicModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${t('topic.edit_title', { defaultValue: 'Edytuj tytuł tematu' })}</h5>
              <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form id="edit-topic-form">
              <div class="modal-body">
                <div class="mb-3">
                  <label class="form-label">${t('topic.title', { defaultValue: 'Tytuł' })}</label>
                  <input type="text" class="form-control" id="edit_topic_title" name="title" required minlength="5" maxlength="255">
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" type="button" data-bs-dismiss="modal">${t('cancel', { defaultValue: 'Anuluj' })}</button>
                <button class="btn btn-primary" type="submit">${t('save', { defaultValue: 'Zapisz' })}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div class="modal fade" id="deleteTopicModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${t('moderation.delete_topic', { defaultValue: 'Usuń temat' })}</h5>
              <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p class="mb-0">${t('moderation.confirm_delete_topic', { defaultValue: 'Czy na pewno usunąć ten temat i wszystkie posty? Tej operacji nie można cofnąć.' })}</p>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" type="button" data-bs-dismiss="modal">${t('cancel', { defaultValue: 'Anuluj' })}</button>
              <button class="btn btn-danger" type="button" id="delete-topic-confirm-btn">
                <i class="fa-solid fa-trash me-1"></i>${t('moderation.delete_topic', { defaultValue: 'Usuń temat' })}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal fade" id="editPostModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${t('post.edit', { defaultValue: 'Edytuj post' })}</h5>
              <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form id="edit-post-form">
              <div class="modal-body">
                <input type="hidden" id="edit_post_id" value="">
                <div class="mb-3">
                  <label class="form-label">${t('content_format', { defaultValue: 'Format treści' })}</label>
                  <select class="form-select" id="edit_post_content_format" name="content_format">
                    <option value="html">${t('content_formats.html', { defaultValue: 'HTML' })}</option>
                    <option value="markdown">${t('content_formats.markdown', { defaultValue: 'Markdown' })}</option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label">${t('post.content')}</label>
                  <textarea class="form-control" rows="8" id="edit_post_raw_content" name="raw_content" required></textarea>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" type="button" data-bs-dismiss="modal">${t('cancel', { defaultValue: 'Anuluj' })}</button>
                <button class="btn btn-primary" type="submit">${t('save', { defaultValue: 'Zapisz' })}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div class="modal fade" id="shareModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${t('post.share_link')}</h5>
              <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="input-group">
                <input class="form-control" id="shareUrlInput" type="text" readonly>
                <button class="btn btn-outline-secondary" id="copyShareUrl" type="button">
                  <i class="fa-solid fa-copy me-1"></i>${t('post.copy_link')}
                </button>
              </div>
              <div class="small text-success mt-2 d-none" id="copySuccess">
                <i class="fa-solid fa-check me-1"></i>${t('post.link_copied')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal fade" id="reportModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">${t('post.report_title')}</h5>
              <button class="btn-close" type="button" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form id="reportForm">
              <div class="modal-body">
                <div class="mb-3">
                  <label class="form-label">${t('post.report_reason')}</label>
                  <select class="form-select" id="report_reason">
                    <option value="spam">${t('report_reasons.spam')}</option>
                    <option value="inappropriate">${t('report_reasons.inappropriate')}</option>
                    <option value="harassment">${t('report_reasons.harassment')}</option>
                    <option value="misinformation">${t('report_reasons.misinformation')}</option>
                    <option value="other">${t('report_reasons.other')}</option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label">${t('post.report_description')}</label>
                  <textarea class="form-control" rows="4" id="report_description" placeholder="${t('post.report_description_placeholder')}"></textarea>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" type="button" data-bs-dismiss="modal">${t('cancel', { defaultValue: 'Anuluj' })}</button>
                <button class="default-btn reverse" type="submit"><span>${t('post.report_submit')}</span></button>
              </div>
            </form>
          </div>
        </div>
      </div>`
  }

  async submitReply(event) {
    event.preventDefault()
    const form = event.target
    const content = form.querySelector('#forum_post_raw_content').value
    const contentFormat = form.querySelector('[name="content_format"]')?.value || 'html'
    const replyToId = form.querySelector('#reply_to_id').value

    if (!content.trim()) return

    const submitBtn = form.querySelector('button[type="submit"]')
    submitBtn.disabled = true

    try {
      const body = { post: { raw_content: content, content_format: contentFormat } }
      if (replyToId) body.post.reply_to_id = replyToId

      const response = await post(`/topics/${this.topicIdValue}/posts`, body)

      form.querySelector('#forum_post_raw_content').value = ''
      form.querySelector('#reply_to_id').value = ''
      const quoteContainer = document.getElementById('quote-container')
      if (quoteContainer) quoteContainer.classList.add('d-none')

      const newPost = normalize(response.data)
      if (newPost && !document.getElementById(`post-${newPost.id}`)) {
        this.appendPost(newPost)
      }
    } catch (e) {
      const alertHtml = `<div class="alert alert-danger alert-dismissible fade show mt-2">${escapeHtml(e.message)}<button class="btn-close" type="button" data-bs-dismiss="alert"></button></div>`
      form.insertAdjacentHTML('beforeend', alertHtml)
    } finally {
      submitBtn.disabled = false
    }
  }

  showErrorModal(message) {
    const existing = document.getElementById('forum-error-modal')
    if (existing) existing.remove()
    const displayMessage = message === 'Internal Server Error'
      ? (t('common.internal_server_error') || 'Wystąpił błąd serwera. Spróbuj ponownie później.')
      : message
    const modalHtml = `
      <div class="modal fade" id="forum-error-modal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header border-0">
              <h5 class="modal-title text-danger"><i class="fa-solid fa-circle-exclamation me-2"></i>${t('common.error') || 'Błąd'}</h5>
              <button class="btn-close" type="button" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body">${escapeHtml(displayMessage)}</div>
            <div class="modal-footer border-0">
              <button class="btn btn-primary" type="button" data-bs-dismiss="modal">${t('cancel') || 'OK'}</button>
            </div>
          </div>
        </div>
      </div>`
    document.body.insertAdjacentHTML('beforeend', modalHtml)
    const modal = document.getElementById('forum-error-modal')
    const bsModal = new bootstrap.Modal(modal)
    modal.addEventListener('hidden.bs.modal', () => modal.remove(), { once: true })
    bsModal.show()
  }

  async topicAction(action) {
    const actions = {
      close: () => post(`/topics/${this.topicIdValue}/close`),
      reopen: () => del(`/topics/${this.topicIdValue}/close`),
      archive: () => post(`/topics/${this.topicIdValue}/archive`),
      delete: () => del(`/topics/${this.topicIdValue}`)
    }
    const fn = actions[action]
    if (!fn) return
    if (action === 'delete') {
      bootstrap.Modal.getInstance(document.getElementById('deleteTopicModal'))?.hide()
    }
    try {
      await fn()
      if (action === 'delete') {
        window.location.href = '/forum'
      } else {
        this.load()
      }
    } catch (e) {
      this.showErrorModal(e.message)
    }
  }

  async openEditModal(postId) {
    try {
      const resp = await get(`/posts/${postId}`)
      const p = normalize(resp.data)
      if (!p) return
      document.getElementById('edit_post_id').value = postId
      document.getElementById('edit_post_raw_content').value = p.raw_content || ''
      document.getElementById('edit_post_content_format').value = p.content_format || 'html'
      const modal = document.getElementById('editPostModal')
      if (modal && window.bootstrap) {
        const modalInstance = new bootstrap.Modal(modal)
        modalInstance.show()
        modal.addEventListener('shown.bs.modal', () => this.initEditModalEditor(), { once: true })
        modal.addEventListener('hidden.bs.modal', () => this.destroyEditModalEditor(), { once: true })
      }
    } catch (e) {
      console.error('Failed to load post:', e.message)
    }
  }

  initEditModalEditor() {
    this.destroyEditModalEditor()
    const textarea = document.getElementById('edit_post_raw_content')
    const formatSelect = document.getElementById('edit_post_content_format')
    if (!textarea || !formatSelect) return

    const format = formatSelect.value
    if (format === 'markdown' && typeof EasyMDE !== 'undefined') {
      this.editModalEasyMDE = new EasyMDE({
        element: textarea,
        spellChecker: false,
        autosave: { enabled: false },
        toolbar: ['bold', 'italic', 'heading', '|', 'quote', 'unordered-list', 'ordered-list', '|', 'link', '|', 'preview', 'side-by-side', '|', 'guide'],
        minHeight: '200px',
        status: false
      })
    } else if (format === 'html' && typeof tinymce !== 'undefined') {
      tinymce.remove('#edit_post_raw_content')
      tinymce.init({
        selector: '#edit_post_raw_content',
        height: 250,
        menubar: false,
        plugins: 'lists link code',
        toolbar: 'undo redo | formatselect | bold italic | bullist numlist | link | removeformat | code',
        content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
        promotion: false,
        branding: false
      })
      this.editModalTinymce = tinymce.get('edit_post_raw_content')
    }

    this.editModalFormatHandler = () => {
      this.destroyEditModalEditor()
      this.initEditModalEditor()
    }
    formatSelect.addEventListener('change', this.editModalFormatHandler)
  }

  destroyEditModalEditor() {
    const formatSelect = document.getElementById('edit_post_content_format')
    if (formatSelect && this.editModalFormatHandler) {
      formatSelect.removeEventListener('change', this.editModalFormatHandler)
      this.editModalFormatHandler = null
    }
    if (this.editModalEasyMDE) {
      const content = this.editModalEasyMDE.value()
      this.editModalEasyMDE.toTextArea()
      this.editModalEasyMDE = null
      const ta = document.getElementById('edit_post_raw_content')
      if (ta) ta.value = content
    }
    if (this.editModalTinymce) {
      this.editModalTinymce.remove()
      this.editModalTinymce = null
    } else if (typeof tinymce !== 'undefined') {
      tinymce.remove('#edit_post_raw_content')
    }
  }

  syncEditModalContentToTextarea() {
    const ta = document.getElementById('edit_post_raw_content')
    if (!ta) return
    if (this.editModalEasyMDE) {
      ta.value = this.editModalEasyMDE.value()
    } else if (this.editModalTinymce) {
      ta.value = this.editModalTinymce.getContent()
    } else if (typeof tinymce !== 'undefined') {
      const ed = tinymce.get('edit_post_raw_content')
      if (ed) ta.value = ed.getContent()
    }
  }

  async submitEditPost(event) {
    event.preventDefault()
    this.destroyEditModalEditor()
    const postId = document.getElementById('edit_post_id').value
    const content = document.getElementById('edit_post_raw_content').value
    const contentFormat = document.getElementById('edit_post_content_format').value
    if (!postId || !content.trim()) return

    const submitBtn = event.target.querySelector('button[type="submit"]')
    submitBtn.disabled = true
    try {
      const updated = await put(`/posts/${postId}`, {
        post: { raw_content: content, content_format: contentFormat }
      })
      const p = normalize(updated.data)
      const postBlock = document.getElementById(`post-${postId}`)
      if (postBlock && p) {
        const contentInner = postBlock.querySelector('.forum-post-content-inner')
        if (contentInner) contentInner.innerHTML = p.rendered_content || p.raw_content || ''
        const editedEl = postBlock.querySelector('.forum-post-edited')
        if (p.edit_count > 0) {
          const editedHtml = `<div class="forum-post-edited small text-muted mt-2">${t('post.edited', { count: p.edit_count })}</div>`
          if (editedEl) editedEl.outerHTML = editedHtml
          else {
            const body = postBlock.querySelector('.forum-post-body')
            if (body) {
              const contentInner2 = body.querySelector('.forum-post-content-inner')
              if (contentInner2) contentInner2.insertAdjacentHTML('afterend', editedHtml)
            }
          }
        }
      }
      bootstrap.Modal.getInstance(document.getElementById('editPostModal'))?.hide()
    } catch (e) {
      const alertHtml = `<div class="alert alert-danger alert-dismissible fade show">${escapeHtml(e.message)}<button class="btn-close" type="button" data-bs-dismiss="alert"></button></div>`
      document.getElementById('editPostModal').querySelector('.modal-body').insertAdjacentHTML('afterbegin', alertHtml)
    } finally {
      submitBtn.disabled = false
    }
  }

  async submitEditTopic(event) {
    event.preventDefault()
    const title = document.getElementById('edit_topic_title').value?.trim()
    if (!title || title.length < 5) return

    const submitBtn = event.target.querySelector('button[type="submit"]')
    submitBtn.disabled = true
    try {
      await put(`/topics/${this.topicIdValue}`, { topic: { title } })
      this.topicData = { ...this.topicData, title }
      const titleEl = this.element.querySelector('.forum-topic-title')
      if (titleEl) titleEl.textContent = title
      bootstrap.Modal.getInstance(document.getElementById('editTopicModal'))?.hide()
    } catch (e) {
      this.showErrorModal(e.message)
    } finally {
      submitBtn.disabled = false
    }
  }

  async toggleReaction(btn) {
    const postId = btn.dataset.postId
    const reactionType = btn.dataset.reactionType
    if (!postId || !reactionType) return

    const container = this.element.querySelector(`[data-post-reactions="${postId}"]`)
    if (!container) return

    const state = JSON.parse(container.dataset.reactionState || '{}')
    const { summary = {}, userReactions = [], canReact = false, isOwnPost = false } = state
    const isActive = userReactions.includes(reactionType)

    btn.disabled = true

    try {
      if (isActive) {
        await del(`/posts/${postId}/reactions/${reactionType}`)
        summary[reactionType] = Math.max(0, (summary[reactionType] || 1) - 1)
        if (summary[reactionType] === 0) delete summary[reactionType]
        const idx = userReactions.indexOf(reactionType)
        if (idx !== -1) userReactions.splice(idx, 1)
      } else {
        await post(`/posts/${postId}/reactions`, { reaction_type: reactionType })
        summary[reactionType] = (summary[reactionType] || 0) + 1
        userReactions.push(reactionType)
      }

      const newState = { summary, userReactions, canReact, isOwnPost }
      container.dataset.reactionState = JSON.stringify(newState)
      container.innerHTML = this.renderReactionButtons(summary, userReactions, postId, canReact, isOwnPost)
      this.bindReactionEvents()
    } catch (e) {
      console.error('Reaction failed:', e.message)
    }
  }

  bindReactionEvents() {
    this.element.querySelectorAll('.forum-reaction-btn:not(.forum-reaction-more), .forum-reaction-dropdown-item').forEach(btn => {
      if (btn._reactionBound) return
      btn._reactionBound = true
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        this.toggleReaction(btn)
      })
    })
  }

  bindEvents() {
    this.element.querySelectorAll('.js-sort').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        this.sortOrder = link.dataset.sort
        this.currentPage = 1
        this.load()
      })
    })

    this.element.querySelectorAll('.js-per-page').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        this.perPage = parseInt(link.dataset.perPage, 10)
        localStorage.setItem('forum_per_page', this.perPage)
        this.currentPage = 1
        this.load()
      })
    })

    this.element.querySelectorAll('[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        this.currentPage = parseInt(link.dataset.page, 10)
        this.loadReplies()
      })
    })

    this.element.querySelectorAll('.js-quote-post').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const postId = link.dataset.postId
        const author = link.dataset.author
        const content = link.dataset.content

        const quoteContainer = document.getElementById('quote-container')
        const quoteLink = document.getElementById('quote-link')
        const quoteText = document.getElementById('quote-text')
        const replyToId = document.getElementById('reply_to_id')
        const textarea = document.getElementById('forum_post_raw_content')

        if (!quoteContainer) return

        replyToId.value = postId
        quoteLink.textContent = `${author}:`
        quoteLink.href = `#post-${postId}`
        quoteText.textContent = content
        quoteContainer.classList.remove('d-none')

        const replyForm = document.getElementById('reply-form')
        if (replyForm) {
          replyForm.scrollIntoView({ behavior: 'smooth' })
          setTimeout(() => { if (textarea) textarea.focus() }, 500)
        }
      })
    })

    this.element.querySelectorAll('.js-remove-quote').forEach(btn => {
      btn.addEventListener('click', () => {
        document.getElementById('quote-container')?.classList.add('d-none')
        const quoteLink = document.getElementById('quote-link')
        if (quoteLink) { quoteLink.textContent = ''; quoteLink.href = '#' }
        const quoteText = document.getElementById('quote-text')
        if (quoteText) quoteText.textContent = ''
        const replyToId = document.getElementById('reply_to_id')
        if (replyToId) replyToId.value = ''
      })
    })

    this.element.querySelectorAll('.js-share-post').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const shareUrl = link.dataset.shareUrl
        const shareInput = document.getElementById('shareUrlInput')
        if (shareInput) shareInput.value = shareUrl
        const modal = document.getElementById('shareModal')
        if (modal && window.bootstrap) {
          new bootstrap.Modal(modal).show()
        }
      })
    })

    const copyBtn = document.getElementById('copyShareUrl')
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const input = document.getElementById('shareUrlInput')
        navigator.clipboard.writeText(input.value).then(() => {
          const success = document.getElementById('copySuccess')
          if (success) {
            success.classList.remove('d-none')
            setTimeout(() => success.classList.add('d-none'), 3000)
          }
        })
      })
    }

    let reportPostId = null
    this.element.querySelectorAll('.js-report-post').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        reportPostId = link.dataset.postId
        const modal = document.getElementById('reportModal')
        if (modal && window.bootstrap) {
          new bootstrap.Modal(modal).show()
        }
      })
    })

    const reportForm = document.getElementById('reportForm')
    if (reportForm) {
      reportForm.addEventListener('submit', async (e) => {
        e.preventDefault()
        if (!reportPostId) return
        try {
          await post(`/posts/${reportPostId}/flags`, {
            flag: {
              reason: document.getElementById('report_reason').value,
              description: document.getElementById('report_description').value
            }
          })
          const modal = bootstrap.Modal.getInstance(document.getElementById('reportModal'))
          if (modal) modal.hide()
          reportPostId = null
        } catch (err) {
          this.showErrorModal(err.message)
        }
      })
    }

    this.element.querySelectorAll('.js-edit-topic-title').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        document.getElementById('edit_topic_title').value = this.topicData?.title || ''
        const modal = document.getElementById('editTopicModal')
        if (modal && window.bootstrap) new bootstrap.Modal(modal).show()
      })
    })
    this.element.querySelectorAll('.js-topic-close').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.topicAction('close') })
    })
    this.element.querySelectorAll('.js-topic-reopen').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.topicAction('reopen') })
    })
    this.element.querySelectorAll('.js-topic-archive').forEach(btn => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.topicAction('archive') })
    })
    this.element.querySelectorAll('.js-topic-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const modal = document.getElementById('deleteTopicModal')
        if (modal && window.bootstrap) new bootstrap.Modal(modal).show()
      })
    })
    this.element.querySelectorAll('.js-edit-post').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        this.openEditModal(link.dataset.postId)
      })
    })
    const editTopicForm = document.getElementById('edit-topic-form')
    if (editTopicForm) {
      editTopicForm.addEventListener('submit', (e) => this.submitEditTopic(e))
    }
    const editPostForm = document.getElementById('edit-post-form')
    if (editPostForm) {
      const editSubmitBtn = editPostForm.querySelector('button[type="submit"]')
      if (editSubmitBtn) {
        editSubmitBtn.addEventListener('click', () => this.syncEditModalContentToTextarea(), { capture: true })
      }
      editPostForm.addEventListener('submit', (e) => this.submitEditPost(e))
    }
    const deleteTopicConfirmBtn = document.getElementById('delete-topic-confirm-btn')
    if (deleteTopicConfirmBtn) {
      deleteTopicConfirmBtn.addEventListener('click', () => this.topicAction('delete'))
    }

    this.bindReactionEvents()

    const replyTextarea = document.getElementById('forum_post_raw_content')
    if (replyTextarea) {
      replyTextarea.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          e.preventDefault()
          const form = document.getElementById('forum-reply-form')
          if (form) form.requestSubmit()
        }
      })
    }

    this.element.querySelectorAll('a[href*="#post-"]').forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href')
        if (!href || !href.startsWith('#post-')) return
        e.preventDefault()
        const anchor = href.substring(1)
        const target = document.getElementById(anchor)
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
          history.pushState(null, '', `#${anchor}`)
          target.classList.add('forum-post-highlight')
          setTimeout(() => target.classList.remove('forum-post-highlight'), 2000)
        }
      })
    })
  }

  async loadReplies() {
    try {
      const postsResp = await get(`/topics/${this.topicIdValue}/posts`, {
        page: this.currentPage,
        per_page: this.perPage
      })
      const posts = normalize(postsResp.data) || []
      const allPosts = Array.isArray(posts) ? posts : [posts]
      const replies = allPosts.filter(p => !p.is_first_post && p.post_number !== 1)
      const canReply = this.topicMeta?.can_reply || false

      const container = document.getElementById('forum-replies-list')
      if (container) {
        container.innerHTML = replies.map((p, i) => {
          const sep = i > 0 ? '<hr class="forum-post-separator">' : ''
          return sep + this.renderPost(p, this.topicData, canReply)
        }).join('')
      }

      const paginationEl = this.element.querySelector('[data-forum-topic-target="pagination"]')
      if (paginationEl) {
        paginationEl.innerHTML = paginationHtml(postsResp.meta)
      }

      this.bindEvents()
      window.scrollTo({ top: document.getElementById('replies-section')?.offsetTop || 0, behavior: 'smooth' })
    } catch (e) {
      console.error('Failed to load replies:', e)
    }
  }
}
